import { useState } from 'react';
import { useReduxState } from 'hooks';
import { EmptyIndicator } from 'components/common';
import { Box } from 'components/common/box';
import { BarChart } from 'components/common/charts';
import { AnalyticsChartLegend } from '.';

interface Props<T extends readonly string[]> {
  colors: Record<T[number], string>;
  data: ({ xLabel: string | number } & Record<T[number], string | number>)[];
  legendDirection?: 'row' | 'column';
  lineLabels: T;
  stacked?: boolean;
  title: string;
}

const AnalyticsBarChart = <T extends readonly string[]>({
  colors,
  data,
  legendDirection = 'row',
  lineLabels,
  stacked = false,
  title
}: Props<T>) => {
  const { messages, theme } = useReduxState();

  const [visibleLines, setVisibleLines] = useState<Set<string>>(
    new Set([...lineLabels])
  );

  const toggleLine = (label: string) => {
    setVisibleLines((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(label)) newSet.delete(label);
      else newSet.add(label);
      return newSet;
    });
  };

  const visibleLineLabels: string[] = lineLabels.filter((label) =>
    visibleLines.has(label)
  );

  // find max y value
  let yMaxValue = 0;
  data?.forEach((item) => {
    const yValue = visibleLineLabels.reduce(
      (sum, label) => sum + item[label],
      0
    );
    if (yValue > yMaxValue) yMaxValue = yValue;
  });

  return (
    <Box padding={0} title={title}>
      {!data.length ? (
        <EmptyIndicator
          icon="chart-bar"
          title={messages.analyticsChooseDateRange}
        />
      ) : (
        <>
          <div className="chart">
            <BarChart
              accessibilityLayer={true}
              data={data}
              stacked={stacked}
              xAxisDataKey="xLabel"
              yAxisDataKey={lineLabels.map((label) => ({
                label,
                color: colors[label]
              }))}
              yAxisDataKeysVisible={visibleLineLabels}
              yAxisDomain={yMaxValue < 12 ? [0, 12] : [0, 'auto']}
            />
          </div>

          <div className="legends">
            <AnalyticsChartLegend
              colors={colors}
              labels={lineLabels}
              labelsVisible={visibleLines}
              legendDirection={legendDirection}
              onLegendItemClick={toggleLine}
            />
          </div>
        </>
      )}

      <style jsx>{`
        .chart {
          padding: 20px;
        }
        .legends {
          border-top: 1px solid ${theme.lightGrey};
          padding: 20px;
        }
      `}</style>
    </Box>
  );
};

export default AnalyticsBarChart;
